import { ReactComponent as Infobulle } from 'core/assets/images/icones/infobulle.svg'
import { ReactComponent as WebIcon } from 'core/assets/images/icones/web.svg'
import { FieldLabel } from 'core/components/common/FieldLabel'
import { FormSelect } from 'core/components/common/FormSelect'
import { Tooltip } from 'core/components/common/Tooltip'
import { PromptForm } from 'core/interfaces/Prompt'
import { TemperatureSlider } from 'lauria/components/common/TemperatureSlider'
import { useModelesLLM } from 'lauria/contexts/ModelesLLMContext'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  ControllerRenderProps,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface PromptFormFieldsProps {
  nomField: ControllerRenderProps<PromptForm, 'nomPrompt'>
  errors: any
  temperatureField: ControllerRenderProps<PromptForm, 'temperature'>
  modeleField: ControllerRenderProps<PromptForm, 'modele'>
  webSearchField: ControllerRenderProps<PromptForm, 'webSearch'>
  setValue: UseFormSetValue<PromptForm>
  register: UseFormRegister<PromptForm>
}

export const PromptFormFields: React.FC<PromptFormFieldsProps> = ({
  nomField,
  errors,
  temperatureField,
  setValue,
  register,
  modeleField,
  webSearchField,
}) => {
  const { t } = useTranslation()
  const [temperature, setTemperature] = useState<number>(0.5)
  const isUpdatingFromForm = useRef(false)
  const { modeles: modeleList } = useModelesLLM()
  const modeleOptions = modeleList.map((modele) => ({
    value: modele.nom,
    label: modele.label,
  }))

  // Synchroniser la température avec la valeur du formulaire (ex: après reset)
  useEffect(() => {
    const formValue = temperatureField.value ?? 0.5
    if (formValue !== temperature && !isUpdatingFromForm.current) {
      setTemperature(formValue)
    }
  }, [temperatureField.value, temperature])

  // Mettre à jour le formulaire quand l'utilisateur change la température
  const handleTemperatureChange = useCallback(
    (value: React.SetStateAction<number>) => {
      isUpdatingFromForm.current = true
      const newTemperature =
        typeof value === 'function' ? value(temperature) : value
      setTemperature(newTemperature)
      setValue('temperature', newTemperature)
      // Reset le flag après un court délai
      setTimeout(() => {
        isUpdatingFromForm.current = false
      }, 0)
    },
    [setValue, temperature]
  )

  return (
    <div className="flex flex-col w-full">
      <div className="flex flex-col w-full flex-grow mb-[10px]">
        <FieldLabel
          className="prompt-field-label"
          label={t('prompts.form.nomPromptLabel')}
          required
        />
        <input
          type="text"
          id="nomPrompt"
          placeholder={t('global.form.input.placeholder')}
          className={`form-input w-full ${errors.nomPrompt ? 'error' : ''}`}
          {...nomField}
        />
        {errors.nomPrompt && (
          <p className="form-error">
            {errors.nomPrompt.type === 'required'
              ? t('global.form.error.mandatory')
              : errors.nomPrompt.message}
          </p>
        )}
      </div>
      <div className="flex flex-col">
        <FieldLabel
          className="md:py-3 h-8 md:h-[30px] mb-[10px]"
          label={t('prompts.form.modeleDispoLabel')}
        />
        <FormSelect
          wrapperClassName="w-full"
          selectClassName="h-[40px] w-full form-input"
          fieldName="modele"
          options={modeleOptions}
          register={register}
          value={
            modeleField.value ||
            modeleList.find((modele) => modele.defaut)?.nom ||
            ''
          }
        />
      </div>
      <div className="flex flex-row items-center justify-between mt-[20px] w-full">
        <div className="flex flex-row items-center gap-x-[10px]">
          <div
            className={`web-search-button ${webSearchField.value ? 'actif' : 'inactif'}`}
            onClick={() => setValue('webSearch', !webSearchField.value)}
          >
            <WebIcon />
            {t('prompts.form.webSearchLabel')}
          </div>
          <div
            className={`${webSearchField.value ? 'text-blue-1350' : 'text-gray-1250'}`}
          >
            {t(
              webSearchField.value
                ? 'prompts.form.actifWebSearchLabel'
                : 'prompts.form.inactiveWebSearchLabel'
            )}
          </div>
          <input
            type="hidden"
            {...webSearchField}
            value={webSearchField.value ? 'true' : 'false'}
          />
        </div>
        <div className="md:flex hidden items-center space-x-1">
          <div>
            <Tooltip text={t('prompts.form.webSearchTooltip')}>
              <span className="text-blue-500 cursor-pointer ml-10px">
                <Infobulle />
              </span>
            </Tooltip>
          </div>
        </div>
      </div>
      <div className="flex flex-col mt-[10px]">
        <FieldLabel
          className="prompt-field-label h-[30px]"
          label={t('prompts.form.temperatureLabel')}
        />
        <TemperatureSlider
          value={temperature}
          setValue={handleTemperatureChange}
          edition
        />
        <input type="hidden" {...temperatureField} />
      </div>
    </div>
  )
}
